import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { Invoice } from '../../../models/invoice';
import { Manager } from '../../../models/manager';
import { Project } from '../../../models/project';
import { Talent } from '../../../models/talent';
import { INVOICES } from '../../../router/constants';
import { invoiceService } from '../../../services/invoiceService';
import { managerService } from '../../../services/managerService';
import { projectService } from '../../../services/projectService';
import { talentService } from '../../../services/talentService';
import InvoiceForm from '../InvoiceForm';

const schema = yup.object({
  projectTitle: yup.string().default('').required('Project Title is required'),
  companyName: yup.string().default('').required('Company Name is required'),
  talentFullName: yup.string().default('').required(),
  manager: yup
    .object()
    .default(null)
    .required('Manager is required')
    .typeError('Manager is required'),
  description: yup.string().default('').nullable(),
  address: yup.string().default('').nullable(),
  category: yup.string().default(''),
  expenseCode: yup.string().default(''),
  expenses: yup.number().default(null).nullable(),
  initials: yup.string().default('').nullable(),
  talentNotes: yup.string().default('').nullable(),
  status: yup.string().default(''),
  createdOn: yup.string().default(''),
  total: yup
    .number()
    .default(null)
    .test('has hourlyRate and totalHours', '', (value, context) => {
      return !(
        !context.parent.hourlyRate &&
        !context.parent.totalHours &&
        !value
      );
    })
    .nullable(),
  hourlyRate: yup
    .number()
    .default(null)
    .test('has totalHours', '', (value, context) => {
      return !(!value && context.parent.totalHours);
    })
    .nullable(),
  totalHours: yup
    .number()
    .default(null)
    .test('has hourlyRate', '', (value, context) => {
      return !(!value && context.parent.hourlyRate);
    })
    .nullable()
});

type CreateInvoiceForm = yup.InferType<typeof schema>;

const defaultValues = schema.getDefault();

const CreateInvoice = (): JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [user, setUser] = useState<Talent | null>(null);
  const [managerList, setManagerList] = useState<Manager[] | undefined>();
  const [isProjectInvoice, setIsProjectInvoice] = useState<boolean>(false);

  const methods = useForm<CreateInvoiceForm>({
    resolver: yupResolver(schema),
    defaultValues
  });

  const { setValue, handleSubmit, reset } = methods;

  useEffect(() => {
    if (params.id) {
      getProject(params.id);
    } else {
      getManagerList();
    }
    getUser();
  }, []);

  useEffect(() => {
    if (project) {
      reset(project as unknown as CreateInvoiceForm);
      setValue(
        'talentFullName',
        `${project.talent?.firstName} ${project.talent?.lastName}` ?? ''
      );
    } else if (user) {
      setValue('companyName', user.company.name ?? '');
      setValue('talentFullName', `${user?.firstName} ${user?.lastName}`);
    }
  }, [project, user]);

  const getProject = (id: string) => {
    projectService.getProjectById(+id).then((response) => {
      setProject(response.data);
      setIsProjectInvoice(true);
    });
  };

  const getUser = () => {
    talentService.getCurrentTalent().then((response) => {
      setUser(response.data);
    });
  };

  const getManagerList = () => {
    managerService
      .getManagerList({ params: { active: true } })
      .then((response) => {
        setManagerList(response.data);
      });
  };

  const handleCreateInvoice = (
    data: CreateInvoiceForm,
    files: File[],
    isDraft = false
  ) => {
    const output = {
      ...data,
      status: isDraft ? 'NOT_YET_SUBMITTED' : 'SUBMITTED',
      projectId: project?.id,
      hourlyRate: data.hourlyRate ?? null,
      totalHours: data.totalHours ?? null
    } as Invoice;

    invoiceService
      .createInvoice({ data: output, isDraft })
      .then((response) => {
        navigate(INVOICES);
        if (files.length && response?.data?.id) {
          const formDataFiles = new FormData();
          files.forEach((file) => {
            formDataFiles.append('files', file);
          });
          invoiceService
            .saveProjectFiles(response.data.id, formDataFiles)
            .then(() => navigate(INVOICES));
        }
      })
      .finally(() => {
        if (params?.closeProject === 'close' && project?.id) {
          projectService.closeProjectById(project.id).then();
        }
      });
  };

  return (
    <>
      <PageTitle>Create Invoice</PageTitle>
      <FormProvider {...methods}>
        <form>
          <InvoiceForm
            title="New Invoice"
            managerList={managerList}
            isProjectInvoice={isProjectInvoice}
            handleSubmit={(files: File[], isDraft?: boolean) =>
              handleSubmit((data) =>
                handleCreateInvoice(data, files, isDraft)
              )()
            }
          />
        </form>
      </FormProvider>
    </>
  );
};

export default CreateInvoice;
